import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Heart, Users, GraduationCap, Globe2, Sparkles, Calendar, MapPin } from "lucide-react";
import api from "@/lib/api";
import PartnersStrip from "@/components/PartnersStrip";

const HERO = "https://images.pexels.com/photos/3030287/pexels-photo-3030287.jpeg?auto=compress&cs=tinysrgb&dpr=2&w=1800";
const ABOUT_IMG = "https://images.unsplash.com/photo-1509099863731-ef4bff19e808?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";
const VOLUNTEER_IMG = "https://images.pexels.com/photos/9491008/pexels-photo-9491008.jpeg?auto=compress&cs=tinysrgb&dpr=2&w=1800";

export default function Home() {
  const [events, setEvents] = useState([]);
  useEffect(() => { api.get("/events").then((r) => setEvents((r.data || []).slice(0, 3))).catch(() => {}); }, []);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative min-h-[88vh] flex items-center text-white overflow-hidden">
        <img src={HERO} alt="BCOL community" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0B1B3D]/95 via-[#0B1B3D]/70 to-[#0B1B3D]/20" />
        <div className="relative max-w-7xl mx-auto px-5 lg:px-8 py-24 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-8">
            <div className="flex items-center gap-3 mb-6">
              <div className="kente-line" />
              <span className="eyebrow text-[var(--bcol-gold)]">Basse Côte Ohio Lagni</span>
            </div>
            <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl leading-[1.05] mb-6">
              Empowering <span className="text-[var(--bcol-gold)]">Community.</span><br />
              Celebrating Culture.<br />
              Inspiring Excellence.
            </h1>
            <p className="text-lg text-white/85 max-w-2xl leading-relaxed mb-8">
              BCOL is a nonprofit organization dedicated to strengthening communities, preserving Guinean heritage, supporting educational achievement, and empowering the African diaspora through service, leadership, and engagement.
            </p>
            <div className="flex flex-wrap gap-4">
              <Link to="/donate" className="btn-gold" data-testid="hero-donate-button"><Heart className="w-4 h-4" /> Donate Today</Link>
              <Link to="/volunteer" className="btn-outline !border-white !text-white hover:!bg-white hover:!text-[var(--bcol-navy)]" data-testid="hero-volunteer-button">Become a Volunteer <ArrowRight className="w-4 h-4" /></Link>
            </div>
          </div>
          <div className="hidden lg:block lg:col-span-4">
          </div>
        </div>
      </section>

      {/* MISSION / PILLARS */}
      <section className="section-pad bg-white">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <div className="grid lg:grid-cols-12 gap-12 items-end mb-14">
            <div className="lg:col-span-7">
              <div className="eyebrow mb-3">Our Mission</div>
              <h2 className="font-display text-4xl sm:text-5xl text-[var(--bcol-navy)] leading-tight">
                A community rooted in heritage, lifted by service, and built for the next generation.
              </h2>
            </div>
            <p className="lg:col-span-5 text-[var(--bcol-muted)] text-lg leading-relaxed">
              We work across cultural preservation, education, leadership, and community empowerment — building bridges so families and youth thrive in Ohio while staying connected to their Guinean and West African roots.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { i: Users, t: "Community Engagement", d: "Building spaces where families connect, support one another, and celebrate together." },
              { i: Globe2, t: "Cultural Preservation", d: "Keeping Guinean heritage alive through storytelling, music, and tradition." },
              { i: GraduationCap, t: "Education & Recognition", d: "Recognizing graduates, awarding scholarships, and supporting academic excellence." },
              { i: Sparkles, t: "Leadership Development", d: "Mentoring the next generation of leaders within the Guinean diaspora." },
            ].map(({ i: Icon, t, d }) => (
              <div key={t} className="card-soft p-7" data-testid={`pillar-${t.toLowerCase().replace(/\s/g,'-')}`}>
                <Icon className="w-7 h-7 text-[var(--bcol-gold)] mb-5" />
                <h3 className="font-display text-xl text-[var(--bcol-navy)] mb-2">{t}</h3>
                <p className="text-sm text-[var(--bcol-muted)] leading-relaxed">{d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ABOUT SPLIT */}
      <section className="section-pad bg-[var(--bcol-bg)] kente-bg">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 grid lg:grid-cols-2 gap-16 items-center">
          <div className="relative">
            <img src={ABOUT_IMG} className="rounded-2xl w-full object-cover h-[520px]" alt="BCOL community" />
            <div className="absolute -bottom-6 -right-4 bg-[var(--bcol-navy)] text-white rounded-2xl p-6 max-w-[260px] hidden sm:block">
              <div className="font-display text-3xl text-[var(--bcol-gold)]">2019</div>
              <div className="text-sm text-white/70">Year BCOL was founded by community leaders in Ohio.</div>
            </div>
          </div>
          <div>
            <div className="eyebrow mb-3">Who we are</div>
            <h2 className="font-display text-4xl sm:text-5xl text-[var(--bcol-navy)] leading-tight mb-5">
              Stronger families, prouder culture, brighter futures.
            </h2>
            <p className="text-[var(--bcol-muted)] leading-relaxed mb-4">
              BCOL was founded by a coalition of community elders, professionals, and students in Ohio who believed that the African diaspora deserves a dedicated institution to preserve heritage and nurture excellence.
            </p>
            <p className="text-[var(--bcol-muted)] leading-relaxed mb-7">
              Today, BCOL supports families through cultural programming, recognizes graduates with scholarships, provides resources to newcomers, and advocates for stronger community ties between Guinea, West Africa, and Ohio.
            </p>
            <Link to="/about" className="btn-primary" data-testid="home-about-learn-more">Learn more <ArrowRight className="w-4 h-4" /></Link>
          </div>
        </div>
      </section>

      {/* EVENTS PREVIEW */}
      <section className="section-pad bg-white">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-6 mb-12">
            <div>
              <div className="eyebrow mb-3">Upcoming</div>
              <h2 className="font-display text-4xl sm:text-5xl text-[var(--bcol-navy)]">Events & Programs</h2>
            </div>
            <Link to="/events" className="btn-outline" data-testid="home-events-view-all">View all <ArrowRight className="w-4 h-4" /></Link>
          </div>
          <div className="grid md:grid-cols-3 gap-7">
            {events.length === 0 && [1,2,3].map((i) => <div key={i} className="card-soft h-80 animate-pulse" />)}
            {events.map((e) => (
              <Link to="/events" key={e.id} className="card-soft overflow-hidden block" data-testid={`event-card-${e.id}`}>
                <div className="h-48 overflow-hidden">
                  <img src={e.image_url} alt={e.title} className="w-full h-full object-cover hover:scale-105 transition duration-500" />
                </div>
                <div className="p-6">
                  <div className="flex items-center gap-3 text-xs text-[var(--bcol-muted)] mb-2">
                    <span className="px-2 py-1 bg-[var(--bcol-navy)]/5 text-[var(--bcol-navy)] rounded">{e.category}</span>
                    <span className="flex items-center gap-1"><Calendar className="w-3 h-3" /> {new Date(e.date).toLocaleDateString()}</span>
                  </div>
                  <h3 className="font-display text-xl text-[var(--bcol-navy)] mb-2">{e.title}</h3>
                  <p className="text-sm text-[var(--bcol-muted)] line-clamp-2 mb-3">{e.description}</p>
                  <div className="text-xs text-[var(--bcol-muted)] flex items-center gap-1"><MapPin className="w-3 h-3" /> {e.location}</div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* PARTNERS STRIP */}
      <PartnersStrip title="Our partners & sponsors" subtitle="Standing with us as we serve the community." />

      {/* VOLUNTEER CTA */}
      <section className="relative section-pad text-white overflow-hidden">
        <img src={VOLUNTEER_IMG} className="absolute inset-0 w-full h-full object-cover" alt="" />
        <div className="absolute inset-0 bg-[var(--bcol-navy)]/90" />
        <div className="relative max-w-5xl mx-auto px-5 lg:px-8 text-center">
          <div className="eyebrow text-[var(--bcol-gold)] mb-4">Get Involved</div>
          <h2 className="font-display text-4xl sm:text-5xl leading-tight mb-5">Your hands, hearts, and time shape this community.</h2>
          <p className="text-white/80 max-w-2xl mx-auto mb-8">Whether you have one hour a month or one weekend a year, there's a place for you at BCOL.</p>
          <div className="flex flex-wrap gap-4 justify-center">
            <Link to="/volunteer" className="btn-gold" data-testid="cta-volunteer">Volunteer With Us</Link>
            <Link to="/membership" className="btn-outline !border-white !text-white hover:!bg-white hover:!text-[var(--bcol-navy)]" data-testid="cta-membership">Become a Member</Link>
          </div>
        </div>
      </section>
    </div>
  );
}

function Stat({ n, l }) {
  return (
    <div>
      <div className="font-display text-3xl text-[var(--bcol-gold)]">{n}</div>
      <div className="text-xs text-white/70 mt-1">{l}</div>
    </div>
  );
}
